// src/pages/payroll/DailyAttendanceDetailsPrint.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Printer } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

const useQuery = () => new URLSearchParams(useLocation().search);

const monthName = (m) => {
  const names = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const n = parseInt(m, 10);
  return names[n] || "";
};

const daysInMonth = (month, year) => {
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (!m || !y) return 31;
  return new Date(y, m, 0).getDate(); // month is 1..12 here
};

const buildKeys = (month, year) => {
  const d = daysInMonth(month, year);
  const fixedStart = ["Employee Code", "Employee Name", "Designation"];
  const dayCols = Array.from({ length: d }, (_, i) => String(i + 1));
  const fixedEnd = ["H", "LOP", "CLOP"];
  return [...fixedStart, ...dayCols, ...fixedEnd];
};

const cellClass = (val) => {
  const v = String(val ?? "").toUpperCase();
  if (v === "L" || v === "A") return "cell red";
  if (v === "M") return "cell pink";
  if (v === "M1") return "cell green";
  return "cell";
};

const showText = (val) => {
  const v = String(val ?? "");
  return v === "M1" ? "M" : v;
};

export default function DailyAttendanceDetailsPrint() {
  const qs = useQuery();

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchid") || localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchtype") || localStorage.getItem("branchType") || "HO";

  // OPTIONAL: if you store company header info in localStorage, it will auto-fill
  const companyName = localStorage.getItem("companyName") || "SRC PROJECTS PRIVATE LIMITED";
  const companyAddr1 = localStorage.getItem("companyAddr1") || "4-B,LAKSHMIPURAM,GANDHI ROAD,SALEM-636 007.";
  const companyAddr2 = localStorage.getItem("companyAddr2") || "Regd office : 4-B,LAKSHMIPURAM,GANDHI ROAD,";
  const companyAddr3 = localStorage.getItem("companyAddr3") || "SALEM-636 007.";
  const companyPhone = localStorage.getItem("companyPhone") || "Ph : 0427-2312343,2319140.";
  const companyFax = localStorage.getItem("companyFax") || "Fax : 0427-2312623,2312622.";
  const companyEmail = localStorage.getItem("companyEmail") || "E-mail : srcsalem@yahoo.co.in";
  const companyLogoUrl = localStorage.getItem("companyLogoUrl") || ""; // if you have it

  // query params (same as Details page open)
  const empType = qs.get("empType") || "working";
  const res = empType === "resigned" ? "1" : "0";

  const payload = useMemo(() => {
    return {
      finYearId: Number(finYearId),
      branchType,
      branchId,
      empcode: qs.get("empcode") || "",
      empname: qs.get("empname") || "",
      designation: qs.get("designation") || "",
      branch: qs.get("branch") || "", // selected branch in filters
      fromdate: qs.get("fromDate") || "",
      todate: qs.get("toDate") || "",
      month: qs.get("month") || "",
      year: qs.get("year") || "",
      res,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [loading, setLoading] = useState(false);
  const [header, setHeader] = useState({
    branch: "",
    month: "",
    year: "",
    monthName: "",
  });
  const [rows, setRows] = useState([]);

  const month = header.month || payload.month || "";
  const year = header.year || payload.year || "";

  const keys = useMemo(() => buildKeys(month, year), [month, year]);

  const getValue = (row, k) => {
    // fixed start columns
    if (k === "Employee Code") return row.fld_Employeecode ?? row.fld_employeecode ?? "";
    if (k === "Employee Name") return row.fld_employee ?? row.fld_Employee ?? row.fld_employeename ?? "";
    if (k === "Designation") return row.fld_designationname ?? "";

    // day columns "1".."31" (dynamic list used)
    if (/^\d+$/.test(k)) return row[k] ?? "";

    // fixed end columns
    if (k === "H") return row.H ?? "";
    if (k === "LOP") return row.LOP ?? row.fld_lop ?? "";
    if (k === "CLOP") return row.CLOP ?? row.fld_compulsorylop ?? "";

    return row[k] ?? "";
  };

  const fetchPrintData = async () => {
    if (!payload.finYearId) return;
    try {
      setLoading(true);
      // reuse the same details data API (recommended)
      const resp = await axios.get(`${BASE_URL}/api/daily-attendance-details/data`, {
        params: payload,
      });

      if (resp?.data?.success) {
        setHeader(
          resp.data.header || {
            branch: payload.branch || payload.branchId,
            month: payload.month,
            year: payload.year,
            monthName: monthName(payload.month),
          }
        );
        setRows(resp.data.rows || []);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPrint = () => window.print();

  const branchLabel = header.branch || payload.branch || payload.branchId || "-";
  const monthLabel = header.monthName || monthName(month) || "-";
  const yearLabel = year || "-";

  return (
    <div className="printRoot">
      <style>{`
        :root { --blue1:#eaf2ff; --blue2:#d7e6ff; --blue3:#5a78c8; --grid:#c7d7ff; }
        * { box-sizing: border-box; }
        body { margin: 0; font-family: Arial, Helvetica, sans-serif; }

        .printRoot{
          min-height: 100vh;
          background: #fff;
          padding: 10px;
        }

        .sheet{
          margin: 0 auto;
          width: 1200px;
          max-width: 98vw;
          background: var(--blue1);
          border: 1px solid #cfd9ee;
          padding: 12px 12px 18px;
        }

        .topBar{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap: 12px;
        }

        .logoBox{
          width: 110px;
          height: 90px;
          background: #fff;
          border: 1px solid #cfd9ee;
          display:flex;
          align-items:center;
          justify-content:center;
          overflow:hidden;
        }
        .logoBox img{ width:100%; height:100%; object-fit:contain; }

        .company{
          flex: 1;
          text-align:center;
          padding: 2px 10px;
        }
        .company .name{
          font-weight: 800;
          letter-spacing: .6px;
          color: #7a4a00;
          font-size: 18px;
          margin-top: 2px;
        }
        .company .addr{
          font-size: 12px;
          line-height: 1.35;
          color:#1b1b1b;
          font-weight: 600;
        }
        .company .meta{
          font-size: 12px;
          line-height: 1.35;
          color:#1b1b1b;
          font-weight: 600;
          margin-top: 4px;
        }

        .printBtn{
          width: 42px;
          height: 42px;
          border: 1px solid #7da0ff;
          background: #fff;
          border-radius: 8px;
          display:flex;
          align-items:center;
          justify-content:center;
          cursor:pointer;
        }
        .printBtn:hover{ background:#f3f6ff; }

        .lineInfo{
          display:flex;
          justify-content:space-between;
          align-items:center;
          margin-top: 10px;
          padding: 6px 2px;
          font-size: 13px;
          font-weight: 700;
        }
        .lineInfo .label b{ color:#c00000; }
        .lineInfo .mid b{ color:#0033cc; }
        .lineInfo .right b{ color:#c00000; }

        .tableWrap{
          margin-top: 6px;
          background: #fff;
          border: 1px solid var(--grid);
          overflow-x: auto;
        }

        table{
          border-collapse: collapse;
          width: max-content; /* important for many columns */
          min-width: 100%;
        }

        thead th{
          position: sticky;
          top: 0;
          background: var(--blue2);
          color: #0b1f5d;
          border: 1px solid var(--grid);
          padding: 5px 6px;
          font-size: 12px;
          white-space: nowrap;
          text-align:center;
          font-weight: 800;
        }

        tbody td{
          border: 1px solid var(--grid);
          padding: 6px 6px;
          font-size: 12px;
          white-space: nowrap;
          text-align:center;
        }

        tbody tr:nth-child(even) td{
          background: #f6f9ff;
        }

        td.left{ text-align:left; }
        td.sno{ width: 44px; font-weight: 800; }
        td.code{ width: 110px; font-weight: 800; }
        td.name{ width: 160px; font-weight: 800; }
        td.desig{ width: 140px; font-weight: 800; }

        .cell.red{ color:#d10000; font-weight:800; }
        .cell.pink{ color:#c000c0; font-weight:800; }
        .cell.green{ color:#0a7a0a; font-weight:800; }

        .muted{
          padding: 20px;
          text-align:center;
          font-weight: 800;
          color:#2d3b6a;
        }

        @media print{
          .printRoot{ padding: 0; }
          .sheet{ width: 100%; max-width: 100%; border: none; }
          .printBtn{ display:none; }
          .tableWrap{ overflow: visible; }
          thead th{ position: static; }
        }
      `}</style>

      <div className="sheet">
        <div className="topBar">
          <div className="logoBox">
            {companyLogoUrl ? <img src={companyLogoUrl} alt="logo" /> : null}
          </div>

          <div className="company">
            <div className="name">{companyName}</div>
            <div className="addr">{companyAddr1}</div>
            <div className="addr">{companyAddr2}</div>
            <div className="addr">{companyAddr3}</div>
            <div className="meta">{companyPhone}</div>
            <div className="meta">
              {companyFax} &nbsp; {companyEmail}
            </div>
          </div>

          <button className="printBtn" onClick={onPrint} title="Print">
            <Printer size={18} />
          </button>
        </div>

        <div className="lineInfo">
          <div className="label">
            Branch : <b>{branchLabel}</b>
          </div>
          <div className="mid">
            Month : <b>{monthLabel}</b>
          </div>
          <div className="right">
            Year : <b>{yearLabel}</b>
          </div>
        </div>

        <div className="tableWrap">
          {loading ? (
            <div className="muted">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="muted">No Records Found</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  {keys.map((k) => (
                    <th key={k}>{k}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx}>
                    <td className="sno">{idx + 1}</td>

                    {keys.map((k) => {
                      const v = getValue(r, k);

                      // small alignment improvements for the first 3 columns
                      if (k === "Employee Code") {
                        return (
                          <td key={k} className="code left">
                            {showText(v)}
                          </td>
                        );
                      }
                      if (k === "Employee Name") {
                        return (
                          <td key={k} className="name left">
                            {showText(v)}
                          </td>
                        );
                      }
                      if (k === "Designation") {
                        return (
                          <td key={k} className="desig left">
                            {showText(v)}
                          </td>
                        );
                      }

                      // day & summary cells
                      return (
                        <td key={k} className={cellClass(v)}>
                          {showText(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}